import {
  BOM_ITEM_STATUS,
  getEffectivePriceSlab,
  getSlabUnitPrice,
} from "utils/bomToolUtils";

// Source value meaning "use each line's optimal distributor" (the optimized mix).
export const QUOTE_SOURCE_OPTIMAL = "__optimal__";

export const applyMargin = (amount, marginPct) =>
  (Number(amount) || 0) * (1 + (Number(marginPct) || 0) / 100);

// Margin for a distributor: its own override when one is entered, otherwise
// the default margin. Values are strings straight from the inputs.
export const getMarginFor = (distributor, defaultPct, overrides) => {
  const o = overrides?.[distributor];
  if (o !== undefined && o !== null && String(o).trim() !== "") {
    return Number(o) || 0;
  }
  return Number(defaultPct) || 0;
};

const round = (n, dp) => {
  const f = 10 ** dp;
  return Math.round((Number(n) || 0) * f) / f;
};

// Digikey reports lead time as a bare number of weeks ("1"); other
// distributors already include the unit ("13 Weeks", "91 Days").
const formatLeadTime = (raw) => {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  if (/^\d+(\.\d+)?$/.test(s)) return `${s} ${s === "1" ? "week" : "weeks"}`;
  return s;
};

const pickProduct = (item, source) => {
  if (source === QUOTE_SOURCE_OPTIMAL) return item.optimal ?? null;
  const opt = (item.distributorOptions || []).find(
    (o) => o.distributor === source,
  );
  return opt?.product ?? null;
};

const toNum = (v) => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const hasValue = (v) => toNum(v) !== null;

// HSN codes arrive as "8532.24.00", "85322400" or "853224": compare digits only.
export const normalizeHsn = (code) => String(code ?? "").replace(/\D/g, "");

// Duty % for an HSN code from the account's HSN table: exact code first, then
// the longest matching 6- or 4-digit prefix (the heading/sub-heading level),
// then the account's default duty. Returns { pct, source } where source is
// "hsn" | "prefix" | "default" | null (null = no duty found).
export const lookupDuty = (hsnCode, hsnRates = [], defaultDutyPct = null) => {
  const code = normalizeHsn(hsnCode);
  if (code) {
    const byCode = new Map(
      (hsnRates || []).map((r) => [normalizeHsn(r.hsn_code), r]),
    );
    const exact = byCode.get(code);
    if (exact && hasValue(exact.duty_pct)) {
      return { pct: toNum(exact.duty_pct), source: "hsn" };
    }
    for (const len of [6, 4]) {
      if (code.length <= len) continue;
      const hit = byCode.get(code.slice(0, len));
      if (hit && hasValue(hit.duty_pct)) {
        return { pct: toNum(hit.duty_pct), source: "prefix" };
      }
    }
  }
  if (hasValue(defaultDutyPct)) {
    return { pct: toNum(defaultDutyPct), source: "default" };
  }
  return { pct: null, source: null };
};

// Spread a shipment's freight (INR) over lines by their share of INR value.
// Lines with a freight override keep it; the rest share what's left.
// `lines` are [{ key, value, override }]; returns { [key]: freightLine }.
export const allocateFreight = (lines, totalFreight) => {
  const out = {};
  let fixed = 0;
  let pool = 0;
  for (const l of lines) {
    if (hasValue(l.override)) {
      out[l.key] = toNum(l.override);
      fixed += out[l.key];
    } else {
      pool += Math.max(Number(l.value) || 0, 0);
    }
  }
  const remaining = Math.max((Number(totalFreight) || 0) - fixed, 0);
  for (const l of lines) {
    if (l.key in out) continue;
    const v = Math.max(Number(l.value) || 0, 0);
    out[l.key] = pool > 0 ? (remaining * v) / pool : 0;
  }
  return out;
};

// Line status shown in the quotation:
//   found          priced, stock covers the requested qty
//   partial found  priced, but stock is below the requested qty (still quoted)
//   not found      no price from the chosen source (quoted as TBA)
//   invalid MPN    no distributor returned the part at all (the BOM tool's
//                  "Invalid MPN", BOM_ITEM_STATUS.NO_MPN_FOUND)
export const QUOTE_STATUS = {
  FOUND: "found",
  PARTIAL: "partial found",
  NOT_FOUND: "not found",
  INVALID_MPN: "invalid MPN",
};
export const PRICED_STATUSES = [QUOTE_STATUS.FOUND, QUOTE_STATUS.PARTIAL];

export const lineStatus = (availability, qty) =>
  availability < qty ? QUOTE_STATUS.PARTIAL : QUOTE_STATUS.FOUND;

const unpricedStatus = (item) =>
  item.itemStatus === BOM_ITEM_STATUS.NO_MPN_FOUND
    ? QUOTE_STATUS.INVALID_MPN
    : QUOTE_STATUS.NOT_FOUND;

const parseAvailability = (p) => {
  const n = parseInt(
    String(p.displayAvailability ?? p.availability ?? 0).replace(/\D/g, ""),
    10,
  );
  return Number.isNaN(n) ? 0 : n;
};

// The quotation worksheet (steps 6–15): per-unit landed cost and resell price
// for every BOM line, the same arithmetic as the team's Excel worksheet:
//   INR = unit × FX · Duty = INR × duty% · Landing = INR + duty + freight/unit
//   Margin = Landing × margin% · Resell = Landing + Margin
// Values are kept at full precision; round only for display/export.
//   overrides   { [itemId]: { hsn, dutyPct, freight, marginPct, leadTime } } for this quote
//   settings    account defaults: { defaultMarginPct, distributorMargins,
//               defaultDutyPct, hsnRates: [{ hsn_code, duty_pct }] }
// Percentages are in percent units (12 = 12%).
export const buildQuotationWorksheet = ({
  rawBomItems,
  bomItems,
  source = QUOTE_SOURCE_OPTIMAL,
  fxRate = 1,
  totalFreight = 0,
  settings = {},
  overrides = {},
  priceByRfqQty = false,
}) => {
  const rawById = Object.fromEntries((rawBomItems || []).map((r) => [r.id, r]));
  const rate = Number(fxRate) > 0 ? Number(fxRate) : 0;
  const {
    defaultMarginPct = 0,
    distributorMargins = {},
    defaultDutyPct = null,
    hsnRates = [],
  } = settings;
  let sourceCurrency = "";

  const priced = (bomItems || []).map((item, idx) => {
    const raw = rawById[item.id] || {};
    const ov = overrides[item.id] || {};
    const qty = parseInt(item.actual_qty, 10) || parseInt(item.qty, 10) || 0;
    const base = {
      key: item.id,
      slNo: idx + 1,
      mpn: item.part_number,
      manufacturer: raw.customer_make ?? raw.manufacturer ?? "",
      description: raw.description ?? "",
      qty,
    };

    const p = pickProduct(item, source);
    const slab = p ? getEffectivePriceSlab(p, qty, priceByRfqQty) : null;
    const buyUnit = slab ? getSlabUnitPrice(slab) : 0;
    if (!p || !(buyUnit > 0)) {
      return { ...base, quoted: false, status: unpricedStatus(item) };
    }
    if (!sourceCurrency) sourceCurrency = p.price?.currency ?? "";

    const hsnCode =
      String(ov.hsn ?? "").trim() || String(p.hts_code ?? "").trim();
    const duty = hasValue(ov.dutyPct)
      ? { pct: toNum(ov.dutyPct), source: "line" }
      : lookupDuty(hsnCode, hsnRates, defaultDutyPct);
    const marginPct = hasValue(ov.marginPct)
      ? toNum(ov.marginPct)
      : getMarginFor(p.distributor, defaultMarginPct, distributorMargins);

    const availability = parseAvailability(p);
    return {
      ...base,
      quoted: true,
      status: lineStatus(availability, qty),
      quotedMpn: p.part_number ?? item.part_number,
      quotedMake: p.manufacturer ?? "",
      distributor: p.distributor ?? "",
      buyUnit,
      buyCurrency: p.price?.currency ?? "",
      inrUnit: buyUnit * rate,
      hsnCode,
      hsnOverridden: String(ov.hsn ?? "").trim() !== "",
      dutyPct: duty.pct ?? 0,
      dutySource: duty.source,
      marginPct,
      marginOverridden: hasValue(ov.marginPct),
      freightOverride: ov.freight,
      spq: parseInt(p.price?.multiple, 10) || 1,
      moq: parseInt(p.price?.min_order, 10) || 1,
      availability,
      leadTime: String(ov.leadTime ?? "").trim() !== ""
        ? String(ov.leadTime)
        : formatLeadTime(p.lead_time),
      leadTimeOverridden: String(ov.leadTime ?? "").trim() !== "",
    };
  });

  const quoted = priced.filter((l) => l.quoted);
  const freightByKey = allocateFreight(
    quoted.map((l) => ({
      key: l.key,
      value: l.inrUnit * l.qty,
      override: l.freightOverride,
    })),
    totalFreight,
  );

  const lines = priced.map((l) => {
    if (!l.quoted) return l;
    const { freightOverride, ...rest } = l;
    const dutyUnit = l.inrUnit * (l.dutyPct / 100);
    const freightLine = freightByKey[l.key] ?? 0;
    const freightUnit = l.qty > 0 ? freightLine / l.qty : 0;
    const landingUnit = l.inrUnit + dutyUnit + freightUnit;
    const marginUnit = landingUnit * (l.marginPct / 100);
    const resellUnit = landingUnit + marginUnit;
    return {
      ...rest,
      freightOverridden: hasValue(freightOverride),
      dutyUnit,
      freightLine,
      freightUnit,
      landingUnit,
      marginUnit,
      resellUnit,
      lineTotal: resellUnit * l.qty,
    };
  });

  const sum = (f) =>
    lines.reduce((s, l) => s + (l.quoted ? f(l) : 0), 0);
  const totals = {
    inr: sum((l) => l.inrUnit * l.qty),
    duty: sum((l) => l.dutyUnit * l.qty),
    freight: sum((l) => l.freightLine),
    landing: sum((l) => l.landingUnit * l.qty),
    margin: sum((l) => l.marginUnit * l.qty),
    resell: sum((l) => l.lineTotal),
  };
  totals.marginPct =
    totals.landing > 0 ? (totals.margin / totals.landing) * 100 : 0;

  return {
    lines,
    sourceCurrency,
    quotedCount: quoted.length,
    totalCount: lines.length,
    totals,
  };
};

// Customer price precision (INR). The customer quote, the saved resell price
// and the server's subtotal all use this rounded unit price, so they agree.
export const RESELL_DP = 2;

// Step 16: the customer-facing quotation (no cost, duty or margin columns), in
// the shape the existing PDF/Excel quotation exports take.
export const toCustomerQuote = (worksheet) => {
  const lines = worksheet.lines.map((l) => {
    if (!l.quoted) return l;
    const unitPrice = round(l.resellUnit, RESELL_DP);
    return { ...l, unitPrice, lineTotal: round(unitPrice * l.qty, 2) };
  });
  return {
    lines,
    quotedCount: worksheet.quotedCount,
    totalCount: worksheet.totalCount,
    total: round(
      lines.reduce((s, l) => s + (l.quoted ? l.lineTotal : 0), 0),
      2,
    ),
  };
};

// Worksheet lines → the API's quotation_items payload.
export const toQuotationItems = (worksheet) =>
  worksheet.lines.map((l) =>
    l.quoted
      ? {
          mpn: l.mpn,
          quoted_mpn: l.quotedMpn,
          quoted_make: l.quotedMake,
          distributor: l.distributor,
          req_qty: l.qty,
          hsn_code: l.hsnCode || null,
          buy_unit: l.buyUnit,
          inr_unit: l.inrUnit,
          duty_pct: l.dutyPct,
          duty_unit: l.dutyUnit,
          freight_line: l.freightLine,
          freight_overridden: l.freightOverridden,
          landing_unit: l.landingUnit,
          margin_pct: l.marginPct,
          margin_unit: l.marginUnit,
          resell_unit: round(l.resellUnit, RESELL_DP),
          spq: l.spq,
          moq: l.moq,
          availability: l.availability,
          lead_time: l.leadTime,
          status: l.status,
        }
      : { mpn: l.mpn, req_qty: l.qty, status: l.status },
  );

// A submitted quotation is shown from its saved snapshot, never re-priced.
export const worksheetFromSaved = (quotation) => {
  const n = (v) => (v == null ? 0 : Number(v));
  const lines = (quotation.items || []).map((it) => {
    const base = { key: it.id, slNo: it.sl_no, mpn: it.mpn, qty: n(it.req_qty), manufacturer: "", description: "" };
    if (!PRICED_STATUSES.includes(it.status)) {
      return { ...base, quoted: false, status: it.status || QUOTE_STATUS.NOT_FOUND };
    }
    return {
      ...base,
      quoted: true,
      status: it.status,
      quotedMpn: it.quoted_mpn,
      quotedMake: it.quoted_make,
      distributor: it.distributor,
      buyUnit: n(it.buy_unit),
      buyCurrency: quotation.source_currency || "",
      inrUnit: n(it.inr_unit),
      hsnCode: it.hsn_code || "",
      dutyPct: n(it.duty_pct),
      dutySource: it.duty_pct == null ? null : "line",
      dutyUnit: n(it.duty_unit),
      freightLine: n(it.freight_line),
      freightUnit: n(it.req_qty) > 0 ? n(it.freight_line) / n(it.req_qty) : 0,
      freightOverridden: !!it.freight_overridden,
      landingUnit: n(it.landing_unit),
      marginPct: n(it.margin_pct),
      marginUnit: n(it.margin_unit),
      resellUnit: n(it.resell_unit),
      lineTotal: n(it.line_total),
      spq: n(it.spq) || 1,
      moq: n(it.moq) || 1,
      availability: n(it.availability),
      leadTime: it.lead_time || "",
    };
  });
  const q = lines.filter((l) => l.quoted);
  const sum = (f) => q.reduce((s, l) => s + f(l), 0);
  const totals = {
    inr: sum((l) => l.inrUnit * l.qty),
    duty: sum((l) => l.dutyUnit * l.qty),
    freight: sum((l) => l.freightLine),
    landing: sum((l) => l.landingUnit * l.qty),
    margin: sum((l) => l.marginUnit * l.qty),
    resell: sum((l) => l.lineTotal),
  };
  totals.marginPct = totals.landing > 0 ? (totals.margin / totals.landing) * 100 : 0;
  return {
    lines,
    sourceCurrency: quotation.source_currency || "",
    quotedCount: q.length,
    totalCount: lines.length,
    totals,
  };
};

// Pre-submit review (step 17). Each issue: { level: "error"|"warn", key, text }.
export const reviewQuotation = (worksheet, { fxRate, fxUpdatedAt } = {}) => {
  const issues = [];
  const add = (level, key, text, lineKeys = []) => {
    if (lineKeys.length) issues.push({ level, key, text, lines: lineKeys });
  };
  const ls = worksheet.lines;
  const q = ls.filter((l) => l.quoted);

  if (!(Number(fxRate) > 0)) {
    issues.push({ level: "error", key: "fx", text: "Set the FX rate." });
  } else if (fxUpdatedAt) {
    const ageH = (Date.now() - new Date(fxUpdatedAt).getTime()) / 36e5;
    if (ageH > 24) {
      issues.push({
        level: "warn",
        key: "fx",
        text: "FX rate is more than a day old. Check today's rate.",
      });
    }
  }
  if (q.length === 0) {
    issues.push({ level: "error", key: "none", text: "No priced lines to quote." });
  }
  const keys = (pred) => ls.filter(pred).map((l) => l.slNo);
  add("warn", "unpriced", "Lines with no price (quoted as TBA)", keys((l) => !l.quoted));
  add("warn", "duty", "Lines with no duty % (no HSN match)", keys((l) => l.quoted && l.dutySource === null));
  add("warn", "margin", "Lines with 0% margin", keys((l) => l.quoted && !(l.marginPct > 0)));
  add(
    "warn",
    "mpn",
    "Quoted MPN differs from requested MPN",
    keys(
      (l) =>
        l.quoted &&
        String(l.quotedMpn).toUpperCase() !== String(l.mpn).toUpperCase(),
    ),
  );
  add("warn", "stock", "Stock below requested qty", keys((l) => l.quoted && l.availability < l.qty));
  add(
    "warn",
    "pack",
    "Qty below MOQ or not a multiple of SPQ",
    keys((l) => l.quoted && (l.qty < l.moq || l.qty % l.spq !== 0)),
  );
  return issues;
};
